import { ApolloError } from '@apollo/client';
import { useCascade, useMutation, useTranslation } from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Mutation,
  MutationCreateSubPoolArgs,
} from '../../../../client/graphql';
import { createSubPoolGQL } from '../../../../common/gql/createSubPool.gql';
import { ContractSubPoolLine } from '../../../../composition/line/contract-sub-pool.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { CreateNewSubPoolContractContext } from '../../createNewSubPoolContract.wizard';

type ResultPosibility = any;
// Mutation['createPool'];

export const SummaryStep = ({
  context: { contractBase, informations, masterPoolContractId },
  validate,
  submit,
}: WizardItemType.Config.Component<CreateNewSubPoolContractContext>) => {
  const { t, lang }: any = useTranslation();
  const [created, setCreated] = useState<SummaryFormType.Data.Line[]>();

  const [createSubPoolCall] = useMutation<
    { createSubPool: Mutation['createSubPool'] },
    MutationCreateSubPoolArgs
  >(createSubPoolGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    async () => {
      try {
        const response = await createSubPoolCall({
          variables: {
            input: {
              contractId: masterPoolContractId!,
              newContractSpacesAllocated: contractBase.capacity!, //! Les valiteurs force la présence
              contractName: informations.name1!, //! Les valiteurs force la présence
              startDate: contractBase.startDate!, //! Les valiteurs force la présence
              stopDate: contractBase.endDate!, //! Les valiteurs force la présence
              isEnabled: contractBase.state,
              reference: contractBase.reference,
              description: contractBase.description,
              antiPassBackMode: contractBase.APBMode,
              name2: informations.name2,
              articleCode: contractBase.articleCode!,
            },
          },
        });

        if (
          response?.errors &&
          Array.isArray(response?.errors) &&
          response?.errors.length > 0
        ) {
          throw response?.errors[0]?.message;
        }

        const createSubPool = response?.data?.createSubPool;

        setCreated(() => [
          {
            _id: uuidv4(),
            items: {
              component: (
                <ContractSubPoolLine
                  data={{
                    name1: createSubPool?.name1 || null,
                    name2: createSubPool?.name2 || null,
                    code: createSubPool?.ospContractId || null,
                    status: createSubPool?.isEnabled ? 'ACTIVE' : 'SUSPENDED',
                  }}
                  config={{ action: false }}
                />
              ),
              state: {
                type: 'success',
              },
            },
          },
        ]);

        return { title: 'CreateSubPool', response: createSubPool };
      } catch (error: unknown) {
        setCreated(() => [
          {
            _id: uuidv4(),
            items: {
              component: (
                <ContractSubPoolLine
                  data={{
                    name1: informations.name1!,
                    name2: informations.name2,
                    status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                    code: null,
                  }}
                  config={{ action: false }}
                />
              ),
              state: {
                type: 'error',
                message:
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
              },
            },
          },
        ]);

        return {
          title: 'CreateSubPool',
          response: new Error(
            error instanceof ApolloError ? `${error.message}` : `${error}`,
          ),
        };
      }
    },
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
